import { useState, useEffect } from 'react';
import { 
  Store, 
  Plus, 
  Pencil, 
  Trash2, 
  Search, 
  RefreshCw,
  X,
  Save,
  Link as LinkIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import AdminLayout from '@/components/admin/AdminLayout';
import { platformsAPI } from '@/lib/api';
import { toast } from 'sonner';

const defaultPlatforms = [
  { name: 'Shopee', slug: 'shopee', icon: '🛒', color: '#EE4D2D' },
  { name: 'Mercado Livre', slug: 'mercadolivre', icon: '🤝', color: '#FFE600' },
  { name: 'Magazine Luiza', slug: 'magazineluiza', icon: '💙', color: '#0086FF' },
  { name: 'Amazon', slug: 'amazon', icon: '📦', color: '#FF9900' },
  { name: 'Temu', slug: 'temu', icon: '🎁', color: '#FB7701' },
  { name: 'AliExpress', slug: 'aliexpress', icon: '🌐', color: '#E62E04' },
];

const colorOptions = [
  '#EE4D2D', '#FFE600', '#0086FF', '#FF9900', '#FB7701', '#E62E04',
  '#7B61FF', '#00C853', '#FF5722', '#2196F3', '#9C27B0', '#000000'
];

export const AdminPlatforms = () => {
  const [loading, setLoading] = useState(true);
  const [platforms, setPlatforms] = useState([]);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    icon: '📦',
    color: '#FF5722',
    url: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchPlatforms = async () => {
    setLoading(true);
    try {
      const response = await platformsAPI.getAll();
      setPlatforms(response.data);
    } catch (error) {
      console.error('Error fetching platforms:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlatforms();
  }, []);

  const openCreateModal = () => {
    setEditingPlatform(null);
    setFormData({ name: '', slug: '', icon: '📦', color: '#FF5722', url: '' });
    setModalOpen(true);
  };

  const openEditModal = (platform) => {
    setEditingPlatform(platform);
    setFormData({
      name: platform.name,
      slug: platform.slug,
      icon: platform.icon || '📦',
      color: platform.color || '#FF5722',
      url: platform.url || ''
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.slug) {
      toast.error('Nome e slug são obrigatórios');
      return;
    }
    
    setSaving(true);
    try {
      if (editingPlatform) {
        await platformsAPI.update(editingPlatform.id, formData);
        toast.success('Plataforma atualizada');
      } else {
        await platformsAPI.create(formData);
        toast.success('Plataforma criada');
      }
      setModalOpen(false);
      fetchPlatforms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await platformsAPI.delete(deleteId);
      setPlatforms(platforms.filter(p => p.id !== deleteId));
      toast.success('Plataforma excluída');
    } catch (error) {
      toast.error('Erro ao excluir plataforma');
    } finally {
      setDeleteId(null);
    }
  };

  const handleNameChange = (name) => {
    const slug = name.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '')
      .replace(/(^-|-$)+/g, '');
    
    setFormData({ ...formData, name, slug });
  };

  const seedDefaultPlatforms = async () => {
    try {
      for (const platform of defaultPlatforms) {
        const exists = platforms.find(p => p.slug === platform.slug);
        if (!exists) {
          await platformsAPI.create(platform);
        }
      }
      toast.success('Plataformas padrão criadas');
      fetchPlatforms();
    } catch (error) {
      toast.error('Erro ao criar plataformas');
    }
  };

  const filteredPlatforms = platforms.filter(p => 
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout title="Plataformas">
      <div className="space-y-6">
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Buscar plataforma..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="flex gap-2">
            {platforms.length === 0 && (
              <Button variant="outline" onClick={seedDefaultPlatforms}>
                Criar Padrão
              </Button>
            )}
            <Button onClick={openCreateModal} className="bg-orange-500 hover:bg-orange-600">
              <Plus className="w-4 h-4 mr-2" />
              Nova Plataforma
            </Button>
          </div>
        </div>

        {/* Platforms Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
          </div>
        ) : filteredPlatforms.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="flex flex-col items-center justify-center py-20 text-slate-400">
              <Store className="w-12 h-12 mb-4 opacity-50" />
              <p className="font-medium">Nenhuma plataforma cadastrada</p>
              <p className="text-sm mb-4">Adicione plataformas para organizar seus produtos</p>
              <Button variant="outline" onClick={seedDefaultPlatforms}>
                Criar Plataformas Padrão
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPlatforms.map((platform) => (
              <Card key={platform.id} className="border-slate-200 hover:border-slate-300 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                        style={{ backgroundColor: `${platform.color}20` }}
                      >
                        {platform.icon || '📦'}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{platform.name}</h3>
                        <p className="text-sm text-slate-500">{platform.slug}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {platform.product_count || 0} produtos
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <div 
                        className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
                        style={{ backgroundColor: platform.color }}
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openEditModal(platform)}
                      className="flex-1"
                    >
                      <Pencil className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setDeleteId(platform.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingPlatform ? 'Editar Plataforma' : 'Nova Plataforma'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Ex: Shopee"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Slug</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="Ex: shopee"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Ícone (emoji)</Label>
                <Input
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="🛒"
                  maxLength={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Cor</Label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                        formData.color === color ? 'border-slate-900 scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>URL do site (opcional)</Label>
                <Input
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://www.shopee.com.br"
                  type="url"
                />
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600">
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Salvar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir plataforma?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação não pode ser desfeita. A plataforma será removida permanentemente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminPlatforms;
